import AnimatedCheckmark from '@capturi/assets/images/AnimatedCheckmark.svg'
import ChatBubbleClusterFileUrl from '@capturi/assets/images/ChatBubbleCluster.svg'
import ChordFileUrl from '@capturi/assets/images/chord.svg'
import ColoredBackgroundFileUrl from '@capturi/assets/images/ColoredBackgroundTop.svg'
import LogoText_colored from '@capturi/assets/images/logo/puzzel_conversational_intelligence.svg'
import PuzzelFlowerFileUrl from '@capturi/assets/images/PuzzelFlower.svg'
import PuzzelLogoFileUrl from '@capturi/assets/images/PuzzelLogo.svg'
import type { BoxProps } from '@capturi/ui-components'
import { Box } from '@capturi/ui-components'
import {
  Alert,
  AlertIcon,
  Button,
  Divider,
  Flex,
  FlexProps,
  Link,
  Text,
  VStack,
} from '@chakra-ui/react'
import { css } from '@emotion/react'
import { t } from '@lingui/core/macro'
import { Trans } from '@lingui/react/macro'
import { Variants, motion } from 'framer-motion'
import ky from 'ky'
import { useCallback, useEffect, useState, FC, ReactNode, useRef } from 'react'

// MODE (not import.meta.env.DEV): the dev API proxy is for the local dev server
// only; unit tests run in Vite mode 'test' and keep the direct-API path.
const isDev = import.meta.env.MODE === 'development'
const useProxyCapturiDevProxy =
  import.meta.env.VITE_USE_CAPTURI_DEV_PROXY === 'true' || isDev

declare global {
  interface Window {
    env?: {
      apiUrl: string
    }
  }
}

const prodUrl = 'https://api.capturi.ai'
const prefixUrl = useProxyCapturiDevProxy ? '/api' : prodUrl

const backgroundAnimation: Variants = {
  hidden: { y: '-100%', opacity: 0 },
  visible: {
    y: 0,
    opacity: 1,
    transition: { duration: 0.8, ease: 'easeOut', delay: 0.5 },
  },
}

const puzzelAnimation: Variants = {
  hidden: { y: '100%', opacity: 0, rotate: -85 },
  visible: {
    y: 0,
    opacity: 1,
    rotate: 0,
    transition: { duration: 0.8, ease: 'easeOut', delay: 0.7 },
  },
}

const chatBubbleAnimation: Variants = {
  hidden: { y: '100%', opacity: 0 },
  visible: {
    y: 0,
    opacity: 1,
    transition: { duration: 0.8, ease: 'easeOut', delay: 1.2 },
  },
}

const chordAnimation: Variants = {
  hidden: { x: '-100%', opacity: 0 },
  visible: {
    x: 0,
    opacity: 1,
    transition: { duration: 0.8, ease: 'easeOut', delay: 1.4 },
  },
}

const UnsubscribeCard: FC<BoxProps> = (props) => {
  return (
    <Box
      bg="workspace.contentBackground"
      border="1px solid"
      borderColor="workspace.contentBorder"
      backdropFilter="blur(8px)"
      boxShadow="glassShadow"
      mt={4}
      borderRadius={8}
      px={6}
      py={8}
      {...props}
    />
  )
}

const UnsubscribeArea: FC<FlexProps> = ({ children, ...props }) => {
  return (
    <Flex pos="relative" px={['2', '4', '8']} pb={8} {...props}>
      <Box w="100%" zIndex={1}>
        {children}
      </Box>
    </Flex>
  )
}

const WavesBackground: FC = () => {
  return (
    <Box pos="absolute" bottom={0} top={0} left={0} right={0}>
      <Box
        as={motion.div}
        pos="absolute"
        top={0}
        left={0}
        right={0}
        aria-hidden
        width="100%"
        variants={backgroundAnimation}
        initial="hidden"
        animate="visible"
      >
        <ColoredBackgroundFileUrl />
      </Box>
      <Box
        as={motion.div}
        pos="absolute"
        bottom={0}
        right={0}
        aria-hidden
        width="20vw"
        maxWidth="400px"
        variants={puzzelAnimation}
        initial="hidden"
        animate="visible"
      >
        <PuzzelFlowerFileUrl />
      </Box>
      <Box
        as={motion.div}
        pos="absolute"
        bottom={0}
        left={0}
        aria-hidden
        width="20vw"
        maxWidth="400px"
        variants={chatBubbleAnimation}
        initial="hidden"
        animate="visible"
      >
        <ChatBubbleClusterFileUrl />
      </Box>
      <Box
        as={motion.div}
        pos="absolute"
        top={0}
        left={0}
        aria-hidden
        width="20vw"
        maxWidth="400px"
        variants={chordAnimation}
        initial="hidden"
        animate="visible"
      >
        <ChordFileUrl />
      </Box>
    </Box>
  )
}

const PageWrapper: FC<{ children: ReactNode }> = ({ children }) => (
  <Flex
    h="100%"
    overflowY="scroll"
    justify="center"
    css={css`
      &::-webkit-scrollbar {
        display: none;
      }
    `}
  >
    <WavesBackground />
    <UnsubscribeArea
      w={{ base: '100%', md: '48em' }}
      minH="650px"
      h="100vh"
      as="main"
    >
      <Flex
        align="center"
        direction="column"
        w="100%"
        h="100%"
        justify="center"
      >
        <Box
          flex="0 0 auto"
          filter="drop-shadow(0px 1px 1px rgba(0, 0, 0, 0.10)) drop-shadow(0px 2px 2px rgba(0, 0, 0, 0.09)) drop-shadow(0px 5px 3px rgba(0, 0, 0, 0.05)) drop-shadow(0px 10px 4px rgba(0, 0, 0, 0.01)) drop-shadow(0px 15px 4px rgba(0, 0, 0, 0.00))"
        >
          <LogoText_colored width="200px" color="greys.600" />
        </Box>
        <UnsubscribeCard w="100%" maxW="30em" mt="6vh">
          {children}
        </UnsubscribeCard>
        <Box
          aria-hidden
          pos="absolute"
          bottom={0}
          mb={8}
          w="100%"
          width="90px"
          pt={8}
          mx="auto"
        >
          <PuzzelLogoFileUrl />
        </Box>
      </Flex>
    </UnsubscribeArea>
  </Flex>
)

export const UnsubscribePage: FC = () => {
  const [isLoading, setIsLoading] = useState(false)
  const [isUnsubscribed, setIsUnsubscribed] = useState(false)
  const [isError, setIsError] = useState(false)

  // Grab the 'token' query parameter
  const token = new URLSearchParams(window.location.search).get('token')

  const handleUnsubscribe = useCallback(async () => {
    if (!token) return
    setIsError(false)
    setIsLoading(true)

    try {
      await ky.post(`subscriptions/unsubscribe/${token}?api-version=3.3`, {
        prefixUrl,
        timeout: 10000,
        retry: 2,
      })
      setIsUnsubscribed(true)
    } catch {
      setIsError(true)
    } finally {
      setIsLoading(false)
    }
  }, [token])

  const handleOpenCapturi = useCallback(() => {
    window.open('https://app.capturi.ai', '_blank')
  }, [])

  const hasAttemptedUnsubscribeRef = useRef(false)
  useEffect(() => {
    if (token && !hasAttemptedUnsubscribeRef.current) {
      hasAttemptedUnsubscribeRef.current = true
      handleUnsubscribe()
    }
  }, [token, handleUnsubscribe])

  if (!token) {
    return (
      <PageWrapper>
        <VStack spacing={4} align="center">
          <Text fontSize="xl">
            <Trans>Missing unsubscribe token. Please check your URL.</Trans>
          </Text>
          <Alert status="info">
            <AlertIcon />
            <Text>
              {t`If you're having trouble unsubscribing, please contact us at`}{' '}
              <Link href="mailto:support@capturi.com" color="primary.main">
                support@capturi.com
              </Link>
            </Text>
          </Alert>
        </VStack>
      </PageWrapper>
    )
  }

  return (
    <PageWrapper>
      <VStack spacing={8} align="center">
        {isUnsubscribed && (
          <VStack spacing={4} align="center">
            <Box boxSize="66px !important" m="-10px" as={AnimatedCheckmark} />
            <Text fontSize="2xl" fontWeight="semibold" textAlign="center">
              <Trans>You have now unsubscribed to the report</Trans>
            </Text>
          </VStack>
        )}

        {!isUnsubscribed && isError && (
          <Text fontSize="2xl" fontWeight="semibold" textAlign="center">
            <Trans>
              Failed to unsubscribe. Please try again or contact support.
            </Trans>
          </Text>
        )}

        <Divider />

        {isError ? (
          <Button
            colorScheme="primary"
            fontWeight="semibold"
            size="md"
            onClick={handleUnsubscribe}
            isLoading={isLoading}
            mt={3}
            w="100%"
          >
            <Trans>Retry unsubscribe</Trans>
          </Button>
        ) : (
          <Button
            colorScheme="primary"
            fontWeight="semibold"
            size="md"
            onClick={handleOpenCapturi}
            isLoading={isLoading}
            mt={3}
            w="100%"
          >
            <Trans>Open Conversational Intelligence</Trans>
          </Button>
        )}
      </VStack>
    </PageWrapper>
  )
}
